import { Colors } from "../../config/DesignConstants";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { rUniform } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";

//=============================================================================
// GHOST CONFIGURATION - All tweakable values in one place
//=============================================================================

const GHOST_CONFIG = {
  // Basic stats
  health: 3, // Medium health
  killPoints: 20, // Good point value
  mass: 1.2, // Standard mass

  // Visual
  color: Colors.CYAN,
  secondaryColor: Colors.DARK_CYAN,
  strokeWidth: 2,
  sides: 8, // Octagon shape
  size: 10,

  // Phasing behavior
  initialPhaseDuration: 2.0, // Start invisible for 2 seconds
  phaseDuration: 3.0, // How long each phase lasts
  visibleDuration: 4.0, // How long visible between phases

  // Movement
  maxSpeed: 70, // Medium speed
  acceleration: 35, // Moderate acceleration
  changeDirectionInterval: 1.5, // Change direction every 1.5 seconds
  velocityDamping: -0.2, // Some damping for smooth movement

  // Teleportation
  teleportDistance: 120, // Distance to teleport
  teleportChance: 0.3, // 30% chance to teleport when changing direction
} as const;

type GhostPhase = "phasing_in" | "visible";

//=============================================================================
// GHOST ENEMY CLASS - Phasing enemy with teleportation
//=============================================================================

export class GhostEnemy extends BaseEnemy {
  // Phasing state
  private phase: GhostPhase = "phasing_in";
  private phaseTimer: number = GHOST_CONFIG.initialPhaseDuration;

  // Movement state
  private directionChangeTimer = 0;
  private currentDirection = V(0, 0);

  constructor(position: V2d) {
    super(position, {
      health: GHOST_CONFIG.health,
      killPoints: GHOST_CONFIG.killPoints,
      mass: GHOST_CONFIG.mass,
      visualConfig: {
        points: [
          ...createRegularPolygon(GHOST_CONFIG.sides, GHOST_CONFIG.size),
        ],
        color: GHOST_CONFIG.color,
        secondaryColor: GHOST_CONFIG.secondaryColor,
        strokeWidth: GHOST_CONFIG.strokeWidth,
      },
    });

    // Start with random direction
    this.generateRandomDirection();
  }

  //===========================================================================
  // MAIN BEHAVIOR - Phasing and movement
  //===========================================================================

  onTick(dt: number): void {
    if (!this.body) return;

    this.updatePhasing(dt);
    this.updateMovement(dt);
    this.applyVelocityDamping();
  }

  //===========================================================================
  // PHASING LOGIC - Visibility cycles
  //===========================================================================

  private updatePhasing(dt: number): void {
    this.phaseTimer -= dt;

    // Handle phase transitions
    if (this.phaseTimer <= 0) {
      switch (this.phase) {
        case "phasing_in":
          this.phase = "visible";
          this.phaseTimer = GHOST_CONFIG.visibleDuration;
          this.setVisibility(true);
          break;

        case "visible":
          this.phase = "phasing_in";
          this.phaseTimer = GHOST_CONFIG.phaseDuration;
          this.setVisibility(false);
          this.teleportRandomly(); // Teleport when becoming invisible
          break;
      }
    }
  }

  private setVisibility(visible: boolean): void {
    this.vectorSprite?.setAlpha(visible ? 1.0 : 0.3);

    // Enable/disable collision when invisible
    if (this.body?.shapes) {
      for (const shape of this.body.shapes) {
        shape.sensor = !visible; // Sensor shapes don't collide
      }
    }
  }

  //===========================================================================
  // MOVEMENT LOGIC - Random direction changes with teleportation
  //===========================================================================

  private updateMovement(dt: number): void {
    this.directionChangeTimer -= dt;

    // Change direction periodically
    if (this.directionChangeTimer <= 0) {
      this.changeDirection();
      this.directionChangeTimer = GHOST_CONFIG.changeDirectionInterval;
    }

    // Apply movement force
    this.applyMovementForce();
  }

  private changeDirection(): void {
    // Chance to teleport instead of changing direction
    if (Math.random() < GHOST_CONFIG.teleportChance) {
      this.teleportRandomly();
    }

    this.generateRandomDirection();
  }

  private generateRandomDirection(): void {
    // Generate random direction
    const angle = rUniform(0, Math.PI * 2);
    this.currentDirection = V(Math.cos(angle), Math.sin(angle));
  }

  private applyMovementForce(): void {
    if (!this.body) return;

    const currentVelocity = V(this.body.velocity);
    const desiredVelocity = this.currentDirection.mul(GHOST_CONFIG.maxSpeed);
    const steeringForce = desiredVelocity.sub(currentVelocity);

    // Apply acceleration
    const force = steeringForce
      .normalize()
      .mul(GHOST_CONFIG.acceleration * this.body.mass);

    this.body.applyForce([force.x, force.y]);
  }

  private applyVelocityDamping(): void {
    if (!this.body) return;

    const velocity = V(this.body.velocity);
    const dampingForce = velocity.mul(GHOST_CONFIG.velocityDamping);
    this.body.applyForce([dampingForce.x, dampingForce.y]);
  }

  //===========================================================================
  // TELEPORTATION - Random position changes
  //===========================================================================

  private teleportRandomly(): void {
    if (!this.body || !this.game) return;

    // Get current position
    const currentPos = V(this.body.position);

    // Generate random teleport direction
    const angle = rUniform(0, Math.PI * 2);
    const teleportDirection = V(Math.cos(angle), Math.sin(angle));
    const teleportOffset = teleportDirection.mul(GHOST_CONFIG.teleportDistance);

    // Calculate new position
    const newPosition = currentPos.add(teleportOffset);

    // Apply position change
    this.body.position = [newPosition.x, newPosition.y];

    // Reset velocity to avoid weird physics
    this.body.velocity = [0, 0];
  }

  //===========================================================================
  // PUBLIC INTERFACE - For debugging or external control
  //===========================================================================

  /**
   * Check if ghost is currently visible and can be hit
   */
  isCurrentlyVisible(): boolean {
    return this.phase === "visible";
  }

  /**
   * Override take damage to only work when visible
   */
  takeDamage(damage: number): void {
    // Only take damage when visible
    if (this.isCurrentlyVisible()) {
      super.takeDamage(damage);
    }
  }
}
